import React, { useEffect, useRef, useState } from "react";
import { StepShell } from "@/components/StepShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ISHIHARA_PLATES } from "@/lib/screeningData";
import { useI18n } from "@/i18n";
import { Palette, ChevronLeft, ChevronRight, X, Check } from "lucide-react";

// Pausa fra la risposta e il passaggio alla piastra successiva: abbastanza da vedere il bottone
// accendersi (la risposta è stata registrata), non tanto da far attendere.
const ADVANCE_MS = 300;

export const IshiharaTest = ({ data, update }) => {
  const { t } = useI18n();
  const [idx, setIdx] = useState(0);
  const plate = ISHIHARA_PLATES[idx];
  const responses = data.ishihara.responses;
  const last = ISHIHARA_PLATES.length - 1;

  const advanceTimer = useRef(null);
  const clearAdvance = () => {
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  };
  // Il timer non deve sopravvivere allo smontaggio dello step: scatterebbe su un componente
  // che non c'è più.
  useEffect(() => clearAdvance, []);

  // Ogni spostamento manuale annulla un avanzamento in sospeso: senza, un click sui pallini
  // subito dopo una risposta verrebbe scavalcato dal timer.
  const goTo = (i) => {
    clearAdvance();
    setIdx(Math.max(0, Math.min(last, i)));
  };

  const answer = (val) => {
    const isFirstAnswer = !(responses[plate.id] || "").length;
    update("ishihara", { responses: { ...responses, [plate.id]: val } });
    // Si avanza da soli solo alla *prima* risposta su una piastra. Chi è tornato indietro a
    // correggere un esito sta rivedendo, non somministrando: sbalzarlo in avanti gli
    // toglierebbe di mano il controllo proprio mentre sistema qualcosa.
    if (!isFirstAnswer || idx >= last) return;
    clearAdvance();
    advanceTimer.current = setTimeout(() => {
      advanceTimer.current = null;
      setIdx((i) => Math.min(last, i + 1));
    }, ADVANCE_MS);
  };

  return (
    <StepShell icon={Palette} title={t("ish_title")} subtitle={t("ish_sub")} testId="ishihara-step">
      <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800">{t("ish_plate")} {idx + 1} {t("ish_of")} {ISHIHARA_PLATES.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="ishihara-prev-plate" disabled={idx === 0} onClick={() => goTo(idx - 1)}>
              <ChevronLeft className="w-4 h-4" /> {t("ish_prev")}
            </Button>
            <Button variant="outline" size="sm" data-testid="ishihara-next-plate" disabled={idx === last} onClick={() => goTo(idx + 1)}>
              {t("ish_next")} <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col items-center gap-6">
          <div className="w-full max-w-[360px] aspect-square rounded-md overflow-hidden bg-white flex items-center justify-center select-none">
            <img src={plate.img} alt="Ishihara" data-testid={`ishihara-plate-${plate.number}`}
              className="w-full h-full object-contain" draggable={false}
              style={{ transform: `scale(${plate.zoom || 1})` }} />
          </div>
          <div className="w-full max-w-md">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("ish_prompt")}</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button variant={responses[plate.id] === "yes" ? "default" : "outline"}
                data-testid={`ishihara-recognized-${plate.id}`} onClick={() => answer("yes")}
                className={responses[plate.id] === "yes"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  // `hover:text-emerald-700` è obbligatorio, non ridondante: la variante
                  // `outline` porta con sé `hover:text-accent-foreground`, che è bianco puro, e
                  // tailwind-merge non lo elimina perché `text-emerald-700` non ha il modificatore
                  // `hover:` — chiavi diverse, nessun conflitto da risolvere. Senza questa classe
                  // il testo diventa bianco su `emerald-50` al passaggio del mouse, cioè sparisce.
                  : "text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"}>
                <Check className="w-4 h-4" /> {t("ish_recognized")}
              </Button>
              <Button variant={responses[plate.id] === "no" ? "default" : "outline"}
                data-testid={`ishihara-none-${plate.id}`} onClick={() => answer("no")}
                className={responses[plate.id] === "no"
                  ? "bg-amber-600 hover:bg-amber-700"
                  : "text-amber-700 border-amber-200 hover:bg-amber-50 hover:text-amber-700"}>
                <X className="w-4 h-4" /> {t("ish_not_recognized")}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {ISHIHARA_PLATES.map((p, i) => {
            const answered = (responses[p.id] || "").length > 0;
            return (
              <button key={p.id} data-testid={`ishihara-dot-${i}`} onClick={() => goTo(i)}
                className={`w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
                  i === idx ? "bg-primary text-white" : answered ? "bg-primary/15 text-primary" : "bg-slate-100 text-slate-400"
                }`}>{i + 1}</button>
            );
          })}
        </div>

        <div className="mt-6 space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("notes")}</Label>
          <Textarea data-testid="ishihara-note-input" rows={2} value={data.ishihara.note}
            onChange={(e) => update("ishihara", { note: e.target.value })} placeholder={t("notes_ph")} />
        </div>
      </div>
    </StepShell>
  );
};
